import { HttpClient } from '@angular/common/http';
import { inject, Injectable, NgZone } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { AuthConnect, AuthResult, ProviderOptions } from '@ionic-enterprise/auth';
import { BehaviorSubject, Observable } from 'rxjs';

import { wait } from '../../../helpers';
import { ConfigSettingsService, LogService, MessageBase } from '../../../providers';
import { AuthSessionService } from '../../auth';
import { Authenticator } from '../interface';
import { IdSrvProvider } from '../provider';

enum AuthErrorsToIgnore {
  UserCanceledAuth = 'user canceled auth',
}

@Injectable({
  providedIn: 'root',
})
export class CodeGrantAuthService implements Authenticator {
  private readonly authSession = inject(AuthSessionService);
  private readonly configService = inject(ConfigSettingsService);
  private readonly httpClient = inject(HttpClient);
  private readonly ngZone = inject(NgZone);
  private readonly message = inject(MessageBase);
  private readonly authenticationChange = new BehaviorSubject(false);

  private readonly oauthScopes = 'res.api offline_access';

  public authenticationChange$: Observable<boolean>;

  private _provider!: IdSrvProvider;
  private _providerOptions!: ProviderOptions;

  constructor() {
    this.authenticationChange$ = this.authenticationChange.asObservable();
  }

  private get authBaseAddress(): string | null {
    return this.configService.appSettings.connection?.auth;
  }

  public async authenticate(): Promise<void> {
    try {
      const authResult = await AuthConnect.login(this._provider, this._providerOptions);
      await this.saveAuthResult(authResult);
    } catch (error: unknown) {
      if (error instanceof Error && !Object.values(AuthErrorsToIgnore).includes(error.message as AuthErrorsToIgnore)) {
        const splitedError = error.message.split(' ').pop() as string;
        await this.message.showError({ message: splitedError });
      }
    }
  }

  public async getAccessToken(): Promise<string | undefined> {
    const res = await this.getAuthResult();
    return res?.accessToken;
  }

  public async initialize(): Promise<void> {
    await AuthConnect.setup({
      platform: Capacitor.isNativePlatform() ? 'capacitor' : 'web',
      logLevel: 'DEBUG',
      ios: {
        webView: 'private',
      },
      web: {
        uiMode: 'popup',
        authFlow: 'PKCE',
      },
    });

    return this.setupProviderAndOptions();
  }

  public async isAuthenticated(): Promise<boolean> {
    const authResult = await this.getAuthResult();

    if (!authResult) {
      return false;
    }

    return AuthConnect.isAccessTokenAvailable(authResult);
  }

  public async logout(): Promise<void> {
    let authResult = await this.getAuthResult();
    if (!authResult) {
      authResult = await AuthConnect.buildAuthResult(this._provider, this._providerOptions, {});
    }

    try {
      await AuthConnect.logout(this._provider, authResult);
    } catch (error) {
      console.error(error);
    }
    await this.saveAuthResult(null);
    await this.authSession.clear();

    return;
  }

  private async getAuthResult(): Promise<AuthResult | null> {
    try {
      const authResult = await this.authSession.getAuthResult();

      if (!authResult) {
        await this.onAuthChange(false);
        return null;
      }

      const isAccessTokenAvailable = await AuthConnect.isAccessTokenAvailable(authResult);
      const isAccessTokenExpired = await AuthConnect.isAccessTokenExpired(authResult);

      if (isAccessTokenAvailable && isAccessTokenExpired) {
        return await this.refreshAuthResult(authResult);
      }

      await this.onAuthChange(true);

      return authResult;
    } catch (error) {
      LogService.error(`${error}`);
      return null;
    }
  }

  private async onAuthChange(isAuthenticated: boolean): Promise<void> {
    this.ngZone.run(() => {
      this.authenticationChange.next(isAuthenticated);
    });
  }

  private async refreshAuthResult(authResult: AuthResult): Promise<AuthResult | null> {
    let newAuthResult: AuthResult | null = null;
    if (await AuthConnect.isRefreshTokenAvailable(authResult)) {
      try {
        newAuthResult = await AuthConnect.refreshSession(this._provider, authResult);
      } catch (err) {
        console.error(err);
      }
    }

    await this.saveAuthResult(newAuthResult);
    return newAuthResult;
  }

  private async saveAuthResult(authResult: AuthResult | null): Promise<void> {
    if (authResult) {
      await this.authSession.saveUserDataFromAuthResult(authResult);
      await this.authSession.setAuthResult(authResult);
    } else {
      await this.authSession.clear();
    }

    await this.onAuthChange(authResult !== null);
  }

  private async setupProviderAndOptions(): Promise<void> {
    if (!this.authBaseAddress) {
      LogService.info('no auth config base address');
      await wait(1);
      return this.setupProviderAndOptions();
    }

    this._provider = new IdSrvProvider(this.httpClient, this.configService);
    this._providerOptions = {
      audience: '',
      clientId: this.configService.appSettings.connection.clientId,
      discoveryUrl: `${this.authBaseAddress}/.well-known/openid-configuration`,
      redirectUri: this.configService.appSettings.connection.redirectUri || '/',
      logoutUrl: this.configService.appSettings.connection.postLogoutRedirectUri || '/',
      scope: this.oauthScopes,
    };
  }
}
